import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../api/config';


// --- Icons (Cleaned up) ---
const CalendarIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="ticket-icon"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
);
const MapPinIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="ticket-icon"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
);
const ClockIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="ticket-icon"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
);

// --- Premium Ticket Component ---
const PremiumTicket = ({ event, index }) => (
    <div className="premium-ticket" style={{ animationDelay: `${index * 150}ms` }}>
        
        {/* LEFT: Visual Stub */}
        <div className="ticket-visual">
            <img 
                src={`https://ethiccraft.org/${event.banner_image1}`} 
                alt={event.event_name} 
                className="ticket-bg-image"
                onError={(e) => { e.target.src='https://images.unsplash.com/photo-1470229722913-7ea2d98634ef?q=80&w=800&auto=format&fit=crop'; }}
            />
            <div className="visual-overlay">
                <span className="event-type-badge">Confirmed</span>
                <div className="visual-date">
                    <span className="v-day">{event.event_date.split(' ')[0]}</span>
                    <span className="v-month">{event.event_date.split(' ')[1] || 'EVENT'}</span>
                </div>
            </div>
        </div>

        {/* MIDDLE: Details */}
        <div className="ticket-content">
            <div className="ticket-header">
                <h2 className="t-title">{event.event_name}</h2>
                <div className="t-id">ID: #{event.booking_id || '0000'}</div>
            </div>
            
            <div className="ticket-grid">
                <div className="t-item">
                    <span className="t-label">Date</span>
                    <div className="t-value"><CalendarIcon /> {event.event_date}</div>
                </div>
                <div className="t-item">
                    <span className="t-label">Time</span>
                    <div className="t-value"><ClockIcon /> {event.booking_time}</div>
                </div>
                <div className="t-item full">
                    <span className="t-label">Location</span>
                    <div className="t-value"><MapPinIcon /> {event.venue}</div>
                </div>
            </div>
        </div>

        {/* RIGHT: QR Stub (Perforated) */}
        <div className="ticket-stub">
            <div className="perforation"></div>
            <div className="qr-box">
                {event.qr_code ? (
                    <img src={`${event.qr_code}`} alt="Entry QR" className="qr-image" />
                ) : (
                    <div className="qr-placeholder">Generating...</div>
                )}
            </div>
            <span className="scan-hint">Scan at Entry</span>
        </div>
    </div>
);

const BOOKINGS_API_URL = `${API_BASE_URL}/api/event_bookings/read`;
const EVENTS_API_URL = `${API_BASE_URL}/api/upcoming_events/read`;

const EventBookings = () => {
    const [bookedEvents, setBookedEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    };

    useEffect(() => {
        const fetchBookedEvents = async () => {
            try {
                setLoading(true);
                const userEmail = getCookie('email');
                if (!userEmail) { setBookedEvents([]); return; }

                // Fetch events list + user's bookings filtered by email (student-safe, no JWT needed)
                const [eventsRes, bookingsRes] = await Promise.all([
                    axios.get(EVENTS_API_URL),
                    axios.get(`${API_BASE_URL}/api/event_bookings/read_byEmail`, {
                        params: { email: userEmail },
                        withCredentials: true
                    })
                ]);

                // Handle both direct array and { data: [...] } response shapes
                const allEvents   = (eventsRes.data?.data   ?? eventsRes.data)   ?? [];
                const userBookings = (bookingsRes.data?.data ?? bookingsRes.data) ?? [];

                if (!Array.isArray(userBookings) || !Array.isArray(allEvents)) {
                    setBookedEvents([]);
                    return;
                }

                const eventsMap = allEvents.reduce((acc, ev) => ({ ...acc, [ev.id]: ev }), {});

                const mergedData = userBookings.map(booking => {
                    const details = eventsMap[booking.event_id];
                    if (!details) return null;
                    return {
                        ...booking,
                        event_name: details.name,
                        event_date: details.date,
                        venue: details.venue,
                        banner_image1: details.banner_image1,
                        booking_time: booking.booking_time,
                        qr_code: booking.qr_code_url,
                        certificate_approved: booking.certificate_approved || 0,
                        user_name: booking.user_name || booking.name || userEmail.split('@')[0],
                    };
                }).filter(Boolean);

                setBookedEvents(mergedData);
            } catch (err) {
                console.error('[EventBookings] fetch error:', err);
                setError("Unable to retrieve bookings.");
            } finally {
                setLoading(false);
            }
        };
        fetchBookedEvents();
    }, []);

    if (loading) return <div className="p-loader"><div className="gold-spinner"></div></div>;

    if (error) return (
        <div className="p-error">
            <h3>Something went wrong</h3>
            <button onClick={() => window.location.reload()}>Retry</button>
        </div>
    );

    return (
        <div className="premium-bookings-container">
            <header className="p-header">
                <h1>Event <span className="text-gold">Passes</span></h1>
                <div className="p-divider"></div>
            </header>

            {bookedEvents.length === 0 ? (
                <div className="p-empty">
                    <p>No active tickets found.</p>
                </div>
            ) : (
                <div className="tickets-wrapper">
                    {bookedEvents.map((event, index) => (
                        <div key={event.booking_id || index} style={{ marginBottom: '40px' }}>
                            <PremiumTicket event={event} index={index} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default EventBookings;